import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, LineChart, YAxis, XAxis, Tooltip, Legend, CartesianGrid, Line } from 'recharts'
import Confetti from 'react-confetti'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import {
  TrendingUpIcon, DuplicateIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, CheckCircleIcon, QuestionMarkCircleIcon
} from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import classNames from '@/utils/classNamesLocal'
import SVGIcon from '@/components/icon'
import { TippyTooltip } from '@/shared/ToolTip'
import { daysFrom } from '@/utils/relativeDays'
import Card from '@/shared/Card'
import Loading from '@/shared/Loading'
import ProjectUpdatesIndex from '@/pages/ManageProjects/ProjectUpdatesIndex'
import ProjectCommentsIndex from '@/pages/ManageProjects/ProjectCommentsIndex'
import FeatureModal from '@/pages/ManageProjects/FeatureModal'
import ListingModal from '@/pages/ManageProjects/ListingModal'

/* ---------- pieces ---------- */

// One "needs you" tile. Each tile has its brand color; only the deadline
// changes, green ahead and red once passed.
const TONES = {
  orange: 'border-cccorange',
  green: 'border-green-500',
  red: 'border-red-500',
  blue: 'border-cccblue',
  purple: 'border-cccpurple'
}
const Help = ({ children }) => (
  <TippyTooltip content={<div className='flex flex-col space-y-3 text-gray-600 dark:text-gray-300'>{children}</div>}>
    <QuestionMarkCircleIcon className='h-4 w-4 text-gray-400' aria-hidden='true' />
  </TippyTooltip>
)

const ICON_TEXT = { orange: 'text-cccorange', green: 'text-green-500', red: 'text-red-500', blue: 'text-cccblue', purple: 'text-cccpurple' }
const Tile = ({ to, icon: Icon, value, label, tone = 'blue', action, help }) => {
  const body = <>
    <div className='flex items-center justify-between gap-x-2'>
      <div className='flex min-w-0 items-center gap-x-3'>
        { typeof Icon === 'string' ? <SVGIcon name={Icon} className='h-9 w-9 flex-shrink-0' /> : <Icon className={classNames('h-8 w-8 flex-shrink-0', ICON_TEXT[tone])} /> }
        <span className='truncate text-2xl font-bold leading-none text-gray-900 dark:text-white'>{value}</span>
      </div>
      { action && <span className={classNames('flex-shrink-0 text-xs font-semibold', ICON_TEXT[tone])}>{action}</span> }
    </div>
    <div className='mt-2 flex items-center gap-x-1 text-sm font-medium text-gray-700 dark:text-gray-200'>{label}{ help && <Help>{help}</Help> }</div>
  </>
  const cls = classNames('block rounded-xl border-l-4 bg-white p-3 shadow-sm transition-shadow dark:bg-gray-800', TONES[tone], to && 'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cccblue')
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>
}

const Fact = ({ children, title }) => <span title={title} className='inline-flex items-center gap-x-1 text-sm text-gray-600 dark:text-gray-300'>{children}</span>

const ShareCard = ({ project, rolesText }) => {
  const [, setToast] = useGlobalState('toast')
  const [preview, setPreview] = useState(false)
  const url = `https://www.castingcall.club/projects/${project.slug}`
  const blurb = `Title: ${project.name} ${project.nsfw ? ':18:' : ''}
Deadline: ${project.deadlineFormatted}
Category: ${project.classification} ${project.category}
Roles: ${rolesText}
Link: ${url}`

  const copied = (what) => setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
    <p className='text-sm font-medium text-green-600 dark:text-green-300'>Copied the {what}</p>
    <p className='mt-1 text-sm text-green-500 dark:text-green-300'>That was the best copying I've ever seen. Quick, paste it somewhere!</p>
  </div>)

  return <Card title='Share it'>
    <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
      <code className='min-w-0 flex-1 truncate rounded-md bg-gray-100 px-3 py-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200'>{url}</code>
      <CopyToClipboard text={url} onCopy={() => copied('link')}>
        <button type='button' className='inline-flex items-center justify-center gap-x-1 rounded-md bg-cccblue px-3 py-2 text-sm font-semibold text-white hover:bg-cccblue-alt'>
          <DuplicateIcon className='h-4 w-4' /> Copy link
        </button>
      </CopyToClipboard>
    </div>
    <div className='flex flex-wrap items-center gap-2'>
      <CopyToClipboard text={blurb} onCopy={() => copied('Discord post')}>
        <button type='button' className='inline-flex items-center gap-x-1 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'>
          <DuplicateIcon className='h-4 w-4' /> Copy Discord post
        </button>
      </CopyToClipboard>
      <button type='button' onClick={() => setPreview(!preview)} className='inline-flex items-center gap-x-1 text-sm text-gray-500 hover:text-cccblue dark:text-gray-400'>
        { preview ? 'Hide' : 'Preview' } { preview ? <ChevronUpIcon className='h-4 w-4' /> : <ChevronDownIcon className='h-4 w-4' /> }
      </button>
      <span className='text-xs text-gray-500 dark:text-gray-400'>Formatted for the CCC Discord. Give the roles line a once-over before posting.</span>
    </div>
    { preview && <pre className='overflow-x-auto whitespace-pre-wrap rounded-md bg-gray-100 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200'>{blurb}</pre> }
  </Card>
}

const ProjectLineChart = ({ stats }) => {
  const [theme] = useGlobalState('theme')
  const fillColor = theme === 'dark' ? '#E5E7EB' : '#6D6E71'
  if (!stats || stats.length === 0) {
    return <div className='text-sm italic text-gray-600 dark:text-gray-300'>Nothing charted yet. Views show up here about a day after they happen.</div>
  }
  return <div style={{ minWidth: '300px', height: '260px' }}>
    <ResponsiveContainer>
      <LineChart data={stats} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
        <YAxis stroke={fillColor} />
        <XAxis dataKey='date' stroke={fillColor} />
        <Tooltip />
        <Legend formatter={(value, entry) => <span style={{ color: entry.color }}>{value}</span>} />
        <CartesianGrid stroke={fillColor} strokeOpacity={0.3} />
        <Line name='Views' type='monotone' dataKey='Views' stroke='#f7784e' strokeWidth={3} dot={{ stroke: '#f7784e', strokeWidth: 2, r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  </div>
}

const Analytics = ({ project }) => {
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState([])
  useEffect(() => {
    getRequest(`/api/v3/manage/projects/${project.id}/stats`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      setStats(jsonData.data || [])
    })
  }, [project.id])

  return <Card title={<span className='flex items-center gap-x-1'><TrendingUpIcon className='h-5 w-5' /> Views</span>}
    footer={<Link to={`/manage/projects/${project.id}/analytics`} className='text-sm font-semibold'>All the analytics</Link>}>
    { loading ? <Loading /> : <ProjectLineChart stats={stats} /> }
  </Card>
}

const DraftCard = ({ project }) => (
  <div className='rounded-xl border-2 border-dashed border-cccblue/50 bg-white p-6 text-center dark:bg-gray-800 sm:p-10'>
    <div className='text-4xl'>📝</div>
    <h3 className='mt-3 text-lg font-bold text-gray-900 dark:text-white'>This one's still a draft</h3>
    <p className='mx-auto mt-1 max-w-md text-sm text-gray-600 dark:text-gray-300'>Nobody can see it but you. Finish the setup and I'll run a checklist before it goes live.</p>
    <Link to={`/manage/projects/${project.id}/start`} className='mt-4 inline-flex items-center gap-x-2 rounded-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-6 py-2.5 text-sm font-bold text-white shadow hover:opacity-90 hover:text-white'>
      Finish setting up
    </Link>
  </div>
)

/* ---------- page ---------- */

const ManageProjectsDashboard = () => {
  const [currentUser] = useGlobalState('currentUser')
  const [manageProjectContainer] = useGlobalState('manageProjectContainer')
  const { project, stats, tabs } = manageProjectContainer
  const { getRequest } = useQuery()
  const [views, setViews] = useState(null)
  const published = new window.URLSearchParams(window.location.search).get('published')

  useEffect(() => {
    if (project.status === 'draft') { return }
    getRequest(`/api/v3/manage/projects/${project.id}/view_count`, {}, (err, jsonData) => {
      if (err) { return }
      // Non-premium members get an upsell string here instead of a number.
      if (typeof jsonData.viewCount === 'number') { setViews(jsonData.viewCount) }
    })
  }, [project.id])

  const rolesText = () => {
    const parts = []
    if (project.voiceRolesMale > 0) parts.push(`${project.voiceRolesMale}M`)
    if (project.voiceRolesFemale > 0) parts.push(`${project.voiceRolesFemale}F`)
    if (project.voiceRolesAny > 0) parts.push(`${project.voiceRolesAny}A`)
    Object.entries(project.otherRoles || {}).forEach(([kind, count]) => parts.push(`${count} ${kind}`))
    return parts.join(', ') || 'None'
  }

  if (project.status === 'draft') { return <DraftCard project={project} /> }

  const base = `/manage/projects/${project.id}`
  const toReview = stats.unsortedAuditionsCount || 0
  const uncast = stats.rolesAvailableCount || 0
  const deadlineDays = daysFrom(project.deadlineIso)
  const deadlinePassed = deadlineDays !== null && deadlineDays < 0
  const completed = project.status === 'completed'

  return <div className='space-y-4'>
    { published && <Confetti style={{ position: 'fixed' }} width={window.innerWidth} gravity={0.05} colors={['#f7784e', '#00b0e7', '#6452a2']} numberOfPieces={300} recycle={false} height={window.innerHeight} /> }

    {/* Needs you */}
    <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
      <Tile to={`${base}/submissions`} icon='icon-submissions' value={`${toReview} / ${stats.submissionsCount || 0}`}
        tone='orange'
        label={toReview === 1 ? 'Audition to review' : 'Auditions to review'} action={toReview > 0 ? 'Listen' : null} />
      <Tile to={`${base}/roles`} icon='icon-manage-projects' value={`${uncast} of ${stats.rolesCount}`}
        label={uncast === 1 ? 'Role still open' : 'Roles still open'} action='Roles' />
      <Tile to={`${base}/messages`} icon='icon-messages' tone='purple' value={tabs.messageCount || 0} label={tabs.messageCount === 1 ? 'Message thread' : 'Message threads'} action='Chat' />
      <Tile to={`${base}/edit`} icon='icon-calendar' value={project.deadline}
        tone={deadlinePassed ? 'red' : 'green'}
        label={deadlinePassed ? 'Deadline passed' : 'Deadline'} action={completed ? null : 'Extend'}
        help={<>
          <div>This is the date you set. Talent have until this date to send in a submission.</div>
          <div>You can extend your deadline as long as you need to and you can continue to send people to your project to find collaborators.</div>
        </>} />
    </div>

    {/* Status strip */}
    <div className='flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-white px-4 py-3 shadow-sm dark:bg-gray-800'>
      { !completed && <span className='inline-flex items-center gap-x-1'>
        <ListingModal compact />
        <Help>
          <div>This project will not be searchable on the main Open Projects area after the listing expires ({project.listingExpiresAt}). Even if your project is not listed, it does not impact your deadline.</div>
          <div>Premium members can list their project for up to 12 weeks, while free members get 5 weeks.</div>
        </Help>
      </span> }
      { !completed && project.featured && <FeatureModal compact /> }
      <span className='hidden h-4 w-px bg-gray-200 dark:bg-gray-600 sm:inline-block' aria-hidden='true' />
      <Fact title='The day you opened it'><CheckCircleIcon className='h-4 w-4 text-gray-400' /> Open since {project.listedAt}</Fact>
      { views !== null && <Fact title='Updates once a day'><TrendingUpIcon className='h-4 w-4 text-gray-400' /> {views} views</Fact> }
    </div>

    {/* Feature upsell. Big on purpose: it is the one paid thing on this page. */}
    { !completed && !project.featured && <FeatureModal callout /> }

    <ShareCard project={project} rolesText={rolesText()} />

    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
      <div className='space-y-4'>
        <ProjectUpdatesIndex />
        { currentUser.canViewProfileAnalytics
          ? <Analytics project={project} />
          : <div className='flex items-start gap-x-3 rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300'>
            <SparklesIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-cccpurple' />
            <span>Curious who's looking? <Link to='/subscriptions' className='font-semibold'>Premium</Link> charts your views and shows who stopped by. It also keeps my lights on.</span>
          </div> }
      </div>
      <ProjectCommentsIndex />
    </div>
  </div>
}

export default ManageProjectsDashboard
